import { Suspense, useEffect, lazy as reactLazy } from "react";
import { homePath } from "./lib/ambito";

/** Carica le pagine con un tentativo di ripetizione: dopo un Publish (chunk cambiati) o con rete debole,
 *  il primo import può fallire → riprova, poi ricarica la pagina una sola volta invece di lasciare lo schermo bianco. */
const lazy = (loader) => reactLazy(() => loader().catch(async (err) => {
  await new Promise((r) => setTimeout(r, 1200));
  try { return await loader(); } catch (e2) {
    const k = "chunk-reload";
    if (!sessionStorage.getItem(k)) { sessionStorage.setItem(k, "1"); window.location.reload(); return new Promise(() => {}); }
    throw e2;
  }
}));
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import Layout from "./components/Layout";
const Dashboard = lazy(() => import(/* webpackPrefetch: true */ "./pages/Dashboard"));
const Pratiche = lazy(() => import(/* webpackPrefetch: true */ "./pages/Pratiche"));
const PraticaDetail = lazy(() => import(/* webpackPrefetch: true */ "./pages/PraticaDetail"));
const Clienti = lazy(() => import(/* webpackPrefetch: true */ "./pages/Clienti"));
const Imprese = lazy(() => import(/* webpackPrefetch: true */ "./pages/Imprese"));
const Tecnici = lazy(() => import(/* webpackPrefetch: true */ "./pages/Tecnici"));
const TemplateDocumenti = lazy(() => import(/* webpackPrefetch: true */ "./pages/TemplateDocumenti"));
const Calendario = lazy(() => import(/* webpackPrefetch: true */ "./pages/Calendario"));
const Fatturazione = lazy(() => import(/* webpackPrefetch: true */ "./pages/Fatturazione"));
const Documenti = lazy(() => import(/* webpackPrefetch: true */ "./pages/Documenti"));
const AIAssistant = lazy(() => import(/* webpackPrefetch: true */ "./pages/AIAssistant"));
const ProfiloStudio = lazy(() => import(/* webpackPrefetch: true */ "./pages/ProfiloStudio"));
const Profilo = lazy(() => import(/* webpackPrefetch: true */ "./pages/Profilo"));
const Preventivi = lazy(() => import(/* webpackPrefetch: true */ "./pages/Preventivi"));
const Impostazioni = lazy(() => import(/* webpackPrefetch: true */ "./pages/Impostazioni"));
const ModulisticaNormativa = lazy(() => import(/* webpackPrefetch: true */ "./pages/ModulisticaNormativa"));
const ComputiMetrici = lazy(() => import(/* webpackPrefetch: true */ "./pages/ComputiMetrici"));
const Condomini = lazy(() => import(/* webpackPrefetch: true */ "./pages/Condomini"));
const CondominioDetail = lazy(() => import(/* webpackPrefetch: true */ "./pages/CondominioDetail"));
const BackupPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/Backup"));
const LettureAIPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/LettureAI"));
const ImpresaDashboard = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaDashboard"));
const ImpresaCantieri = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaCantieri"));
const ImpresaMovimenti = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaMovimenti"));
function RedirectTab({ tab }) {
  const loc = useLocation();
  const p = new URLSearchParams(loc.search); p.set("tab", tab);
  return <Navigate to={`/impresa/operai?${p.toString()}`} replace />;
}

const ImpresaSquadra = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaSquadra"));
const ImpresaFornitori = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaFornitori"));
const ImpresaProfilo = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaProfilo"));
const ImpresaScadenzario = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaScadenzario"));
const ImpresaPreventivi = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaPreventivi"));
const ImpresaCantiereMode = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaCantiereMode"));
const ImpresaCantiereDetail = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaCantiereDetail"));
const ImpresaCantiereEstratto = lazy(() => import("./pages/impresa/ImpresaCantiereEstratto"));
const ImpresaIva = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaIva"));
const ImpresaPatrimonio = lazy(() => import(/* webpackPrefetch: true */ "./pages/impresa/ImpresaPatrimonio"));
const ScadenzeProfessionali = lazy(() => import(/* webpackPrefetch: true */ "./pages/ScadenzeProfessionali"));
const VerbaleRapido = lazy(() => import(/* webpackPrefetch: true */ "./pages/VerbaleRapido"));
const Abbonamento = lazy(() => import("./pages/Abbonamento"));
const DashboardStudio = lazy(() => import(/* webpackPrefetch: true */ "./pages/DashboardStudio"));
import ServerStatusBanner from "./components/ServerStatusBanner";
import SplashScreen from "./components/SplashScreen";
import AuthCallback from "./components/AuthCallback";
import "./App.css";

const PageLoader = () => (
  <div className="min-h-[40vh] flex items-center justify-center" data-testid="page-loader">
    <div className="h-6 w-6 rounded-full border-2 border-slate-300 border-t-slate-900 animate-spin" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-loading">
        <div className="text-sm text-slate-500 font-mono">Caricamento...</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return null;
  if (user && user !== false) return <Navigate to={homePath()} replace />;
  return children;
};

function AppRoutes() {
  const location = useLocation();
  useEffect(() => { const t = setTimeout(() => sessionStorage.removeItem("chunk-reload"), 10000); return () => clearTimeout(t); }, []);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to={homePath()} replace />} />
            <Route path="dashboard" element={<DashboardStudio />} />
            <Route path="dashboard/completa" element={<Dashboard />} />
            <Route path="pratiche" element={<Pratiche />} />
            <Route path="verbale-rapido" element={<VerbaleRapido />} />
            <Route path="pratiche/:id" element={<PraticaDetail />} />
            <Route path="clienti" element={<Clienti />} />
            <Route path="imprese" element={<Imprese />} />
            <Route path="tecnici" element={<Tecnici />} />
            <Route path="template-documenti" element={<TemplateDocumenti />} />
            <Route path="calendario" element={<Calendario />} />
            <Route path="fatturazione" element={<Fatturazione />} />
            <Route path="computi-metrici" element={<ComputiMetrici />} />
            <Route path="condomini" element={<Condomini />} />
            <Route path="condomini/:id" element={<CondominioDetail />} />
            <Route path="documenti" element={<Documenti />} />
            <Route path="assistente" element={<AIAssistant />} />
            <Route path="studio" element={<Navigate to="/profilo-studio" replace />} />
            <Route path="compensi" element={<Navigate to="/fatturazione" replace />} />
            <Route path="profilo-studio" element={<ProfiloStudio />} />
            <Route path="profilo" element={<Profilo />} />
            <Route path="preventivi" element={<Preventivi />} />
            <Route path="modulistica-normativa" element={<ModulisticaNormativa />} />
            <Route path="impostazioni" element={<Impostazioni />} />
            <Route path="backup" element={<BackupPage />} />
            <Route path="abbonamento" element={<Abbonamento />} />
            <Route path="abbonamento/successo" element={<Abbonamento />} />
            <Route path="abbonamento/annullato" element={<Abbonamento />} />
            <Route path="letture-ai" element={<LettureAIPage />} />
            <Route path="scadenze-professionali" element={<ScadenzeProfessionali />} />
            <Route path="impresa" element={<ImpresaDashboard />} />
            <Route path="impresa/cantieri" element={<ImpresaCantieri />} />
            <Route path="impresa/movimenti" element={<ImpresaMovimenti />} />
            <Route path="impresa/operai" element={<ImpresaSquadra />} />
            <Route path="impresa/fornitori" element={<ImpresaFornitori />} />
            <Route path="impresa/profilo" element={<ImpresaProfilo />} />
            <Route path="impresa/scadenzario" element={<ImpresaScadenzario />} />
            <Route path="impresa/preventivi" element={<ImpresaPreventivi />} />
            <Route path="impresa/cantiere-mode" element={<ImpresaCantiereMode />} />
            <Route path="impresa/presenze" element={<RedirectTab tab="presenze" />} />
            <Route path="impresa/cantieri/:id" element={<ImpresaCantiereDetail />} />
            <Route path="impresa/cantieri/:id/estratto-conto" element={<ImpresaCantiereEstratto />} />
            <Route path="impresa/buste-paga" element={<RedirectTab tab="buste-paga" />} />
            <Route path="impresa/iva" element={<ImpresaIva />} />
            <Route path="impresa/patrimonio" element={<ImpresaPatrimonio />} />
          </Route>
        </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SplashScreen />
        <ServerStatusBanner />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
